import React, { useEffect, useState, useRef } from 'react';
import { Clock, AlertCircle, CheckCircle, User, Building, Landmark, SmartphoneIcon, BellRingIcon, ShieldCheckIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { formatPrice } from '../../utils/formatters';
interface RaastRTPPaymentProps {
  totalAmount: number;
  onComplete: () => void;
  onCancel: () => void;
}
type RtpStep = 'input' | 'confirm' | 'waiting';
export function RaastRTPPayment({
  totalAmount,
  onComplete,
  onCancel
}: RaastRTPPaymentProps) {
  const [step, setStep] = useState<RtpStep>('input');
  const [idType, setIdType] = useState<'iban' | 'alias'>('alias');
  const [idValue, setIdValue] = useState('');
  const [error, setError] = useState('');
  // Timer state for waiting step
  const [timeLeft, setTimeLeft] = useState(300);
  const [isExpired, setIsExpired] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  // Mock account details
  const mockAccountTitle = 'Muhammad Shoaib';
  const mockBankName = 'Habib Bank Limited';
  const validateIBAN = (value: string): string | null => {
    const clean = value.replace(/\s/g, '').toUpperCase();
    if (clean.length !== 24) {
      return 'IBAN must be exactly 24 characters';
    }
    // Standard IBAN: 2 letter country code + 2 digits + 20 alphanumeric
    const ibanRegex = /^[A-Z]{2}\d{2}[A-Z0-9]{20}$/;
    if (!ibanRegex.test(clean)) {
      return 'Invalid IBAN format. Must start with 2-letter country code followed by 2 digits and 20 alphanumeric characters (e.g. PK36HABB0000001123456702)';
    }
    return null;
  };
  const validateAlias = (value: string): string | null => {
    const clean = value.replace(/\D/g, '');
    if (clean.length !== 11) {
      return 'Mobile number must be exactly 11 digits';
    }
    if (!clean.startsWith('03')) {
      return 'Mobile number must start with 03';
    }
    return null;
  };
  const handleIBANChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Allow only alphanumeric and spaces for IBAN
    const val = e.target.value.replace(/[^a-zA-Z0-9\s]/g, '').toUpperCase();
    setIdValue(val);
    setError('');
  };
  const handleAliasChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Allow only digits for mobile number
    const val = e.target.value.replace(/\D/g, '').slice(0, 11);
    setIdValue(val);
    setError('');
  };
  const handleValidate = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (idType === 'iban') {
      const err = validateIBAN(idValue);
      if (err) {
        setError(err);
        return;
      }
    } else {
      const err = validateAlias(idValue);
      if (err) {
        setError(err);
        return;
      }
    }
    setStep('confirm');
  };
  const handleSendRequest = () => {
    setStep('waiting');
  };
  // Timer logic for waiting step
  useEffect(() => {
    if (step !== 'waiting') return;
    if (timeLeft <= 0) {
      setIsExpired(true);
      return;
    }
    const timerId = setInterval(() => {
      setTimeLeft(prev => prev - 1);
    }, 1000);
    return () => clearInterval(timerId);
  }, [step, timeLeft]);
  // Auto-redirect timer (10 seconds)
  useEffect(() => {
    if (step !== 'waiting' || isExpired) return;
    const successTimerId = setTimeout(() => {
      onCompleteRef.current();
    }, 50000);
    return () => clearTimeout(successTimerId);
  }, [step, isExpired]);
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  return <div className="flex flex-col py-2" data-id="element-608">
      <div className="text-center mb-6" data-id="element-609">
        <img src="https://cdn.magicpatterns.com/uploads/oZFx4MBZmzvJA34NArst7p/RAAST_Logo.png" alt="RAAST" className="h-10 object-contain mx-auto mb-2" data-id="element-610" />
        <h2 className="text-xl font-bold text-gray-900" data-id="element-611">
          Request to Pay (RTP)
        </h2>
      </div>

      {step === 'input' && <form onSubmit={handleValidate} className="space-y-6 max-w-sm mx-auto w-full" data-id="element-612">
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-100 mb-6 flex justify-between items-center" data-id="element-613">
            <div data-id="element-614">
              <p className="text-sm text-blue-800 font-medium" data-id="element-615">
                Payment Amount
              </p>
              <p className="text-xl font-bold text-blue-900" data-id="element-616">
                {formatPrice(totalAmount)}
              </p>
            </div>
          </div>

          <div className="space-y-4" data-id="element-617">
            <label className="block text-sm font-medium text-gray-700" data-id="element-618">
              Select Identifier Type
            </label>
            <div className="grid grid-cols-2 gap-4" data-id="element-619">
              <label className={`border rounded-lg p-3 cursor-pointer flex items-center justify-center transition-colors ${idType === 'alias' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 hover:bg-gray-50'}`} data-id="element-620">
                <input type="radio" name="idType" value="alias" checked={idType === 'alias'} onChange={() => {
              setIdType('alias');
              setIdValue('');
              setError('');
            }} className="sr-only" data-id="element-621" />
                <span className="font-medium text-sm" data-id="element-622">Mobile Number</span>
              </label>
              <label className={`border rounded-lg p-3 cursor-pointer flex items-center justify-center transition-colors ${idType === 'iban' ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 hover:bg-gray-50'}`} data-id="element-623">
                <input type="radio" name="idType" value="iban" checked={idType === 'iban'} onChange={() => {
              setIdType('iban');
              setIdValue('');
              setError('');
            }} className="sr-only" data-id="element-624" />
                <span className="font-medium text-sm" data-id="element-625">IBAN</span>
              </label>
            </div>
          </div>

          <div data-id="element-626">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-627">
              {idType === 'alias' ? 'Enter Mobile Number (Alias)' : 'Enter IBAN'}
            </label>
            <input type="text" value={idValue} onChange={idType === 'alias' ? handleAliasChange : handleIBANChange} placeholder={idType === 'alias' ? '03XXXXXXXXX' : 'PK36HABB0000001123456702'} maxLength={idType === 'alias' ? 11 : 27} className={`w-full px-4 py-3 border rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 font-mono tracking-wide ${error ? 'border-red-500' : 'border-gray-300'}`} data-id="element-628" />
            {error && <p className="mt-1 text-sm text-red-600" data-id="element-629">{error}</p>}
            <p className="mt-2 text-xs text-gray-500" data-id="element-630">
              {idType === 'alias' ? 'Enter 11-digit mobile number starting with 03' : 'Enter 24-character IBAN (e.g. PK36HABB0000001123456702)'}
            </p>
          </div>

          <div className="pt-4 flex flex-col space-y-3" data-id="element-631">
            <button type="submit" disabled={!idValue} className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 transition-colors" data-id="element-632">
              Validate Details
            </button>
            <button type="button" onClick={onCancel} className="w-full flex justify-center py-3 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-633">
              Cancel
            </button>
          </div>
        </form>}

      {step === 'confirm' && <div className="max-w-sm mx-auto w-full" data-id="element-634">
          {/* Verified badge */}
          <div className="flex items-center gap-2 mb-4 bg-green-50 border border-green-200 rounded-lg px-4 py-2.5" data-id="element-635">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" data-id="element-636" />
            <span className="text-sm font-medium text-green-800" data-id="element-637">
              Account Verified Successfully
            </span>
          </div>

          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-6 shadow-sm" data-id="element-638">
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200" data-id="element-639">
              <h3 className="font-semibold text-gray-800" data-id="element-640">Account Details</h3>
            </div>
            <div className="p-4 space-y-4" data-id="element-641">
              <div data-id="element-642">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-643">
                  Account Title
                </p>
                <div className="flex items-center gap-2" data-id="element-644">
                  <User className="w-4 h-4 text-gray-400" data-id="element-645" />
                  <p className="font-semibold text-gray-900" data-id="element-646">
                    {mockAccountTitle}
                  </p>
                </div>
              </div>
              <div data-id="element-647">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-648">
                  Bank Name
                </p>
                <div className="flex items-center gap-2" data-id="element-649">
                  <Landmark className="w-4 h-4 text-gray-400" data-id="element-650" />
                  <p className="font-medium text-gray-900" data-id="element-651">{mockBankName}</p>
                </div>
              </div>
              <div data-id="element-652">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-653">
                  {idType === 'alias' ? 'Mobile Number' : 'IBAN'}
                </p>
                <div className="flex items-center gap-2" data-id="element-654">
                  <Building className="w-4 h-4 text-gray-400" data-id="element-655" />
                  <p className="font-medium text-gray-900 font-mono" data-id="element-656">
                    {idValue}
                  </p>
                </div>
              </div>
              <div className="pt-3 border-t border-gray-100" data-id="element-657">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-658">
                  Amount to Request
                </p>
                <p className="text-lg font-bold text-blue-600" data-id="element-659">
                  {formatPrice(totalAmount)}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 text-blue-800 text-sm p-4 rounded-lg mb-6" data-id="element-660">
            A payment request will be sent to this account. You will need to
            approve it from your banking app.
          </div>

          <div className="flex flex-col space-y-3" data-id="element-661">
            <button onClick={handleSendRequest} className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors" data-id="element-662">
              Send Payment Request
            </button>
            <button onClick={() => setStep('input')} className="w-full flex justify-center py-3 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors" data-id="element-663">
              Back
            </button>
          </div>
        </div>}

      {step === 'waiting' && <div className="text-center max-w-sm mx-auto w-full" data-id="element-664">
          {isExpired ? <div className="bg-red-50 border border-red-200 rounded-xl p-8 flex flex-col items-center" data-id="element-665">
              <AlertCircle className="h-12 w-12 text-red-500 mb-4" data-id="element-666" />
              <h3 className="text-lg font-bold text-red-900 mb-2" data-id="element-667">
                Request Expired
              </h3>
              <p className="text-sm text-red-700 mb-6 text-center" data-id="element-668">
                The payment request was not approved in time. Please try again.
              </p>
              <button onClick={() => {
          setStep('input');
          setTimeLeft(300);
          setIsExpired(false);
        }} className="w-full py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors" data-id="element-669">
                Try Again
              </button>
            </div> : <div className="flex flex-col items-center py-4" data-id="element-670">
              <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center mb-6 relative" data-id="element-671">
                <div className="absolute inset-0 border-4 border-blue-200 rounded-full animate-ping opacity-75" data-id="element-672"></div>
                <CheckCircle className="w-10 h-10 text-blue-600 relative z-10" data-id="element-673" />
              </div>

              <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-674">
                Request Sent Successfully
              </h3>
              <p className="text-sm text-gray-600 mb-6 px-4" data-id="element-675">
                Please open your banking app and approve the payment request of{' '}
                <span className="font-bold" data-id="element-676">{formatPrice(totalAmount)}</span>.
              </p>

              <div className="flex items-center justify-center space-x-2 text-sm font-medium text-gray-700 bg-gray-100 px-6 py-3 rounded-full mb-6" data-id="element-677">
                <Clock className="h-5 w-5 text-blue-600" data-id="element-678" />
                <span data-id="element-679">
                  Expires in:{' '}
                  <span className="text-blue-600 font-bold text-base" data-id="element-680">
                    {formatTime(timeLeft)}
                  </span>
                </span>
              </div>

              {/* User Guidelines */}
              <div className="w-full bg-gray-50 border border-gray-200 rounded-xl mb-6 overflow-hidden text-left" data-id="element-681">
                <button onClick={() => setShowGuide(!showGuide)} className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-gray-700 hover:bg-gray-100 transition-colors" data-id="element-682">
                  <span className="flex items-center gap-2" data-id="element-683">
                    <SmartphoneIcon className="w-4 h-4 text-blue-600" data-id="element-684" />
                    How to approve from your banking app?
                  </span>
                  {showGuide ? <ChevronUpIcon className="w-4 h-4 text-gray-500" data-id="element-685" /> : <ChevronDownIcon className="w-4 h-4 text-gray-500" data-id="element-686" />}
                </button>
                {showGuide && <div className="px-4 pb-4 pt-1 border-t border-gray-200" data-id="element-687">
                    <div className="space-y-4" data-id="element-688">
                      <div className="flex items-start gap-3" data-id="element-689">
                        <div className="w-7 h-7 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-690">
                          <span className="text-xs font-bold text-blue-700" data-id="element-691">
                            1
                          </span>
                        </div>
                        <div data-id="element-692">
                          <p className="text-sm font-medium text-gray-800" data-id="element-693">
                            Open your banking app
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5" data-id="element-694">
                            Launch your bank's mobile app (e.g. HBL Mobile,
                            JazzCash, Easypaisa)
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3" data-id="element-695">
                        <div className="w-7 h-7 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-696">
                          <span className="text-xs font-bold text-blue-700" data-id="element-697">
                            2
                          </span>
                        </div>
                        <div data-id="element-698">
                          <p className="text-sm font-medium text-gray-800 flex items-center gap-1" data-id="element-699">
                            Check notifications
                            <BellRingIcon className="w-3.5 h-3.5 text-amber-500" data-id="element-700" />
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5" data-id="element-701">
                            You will receive a RAAST payment request
                            notification. Tap on it to view details.
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3" data-id="element-702">
                        <div className="w-7 h-7 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-703">
                          <span className="text-xs font-bold text-blue-700" data-id="element-704">
                            3
                          </span>
                        </div>
                        <div data-id="element-705">
                          <p className="text-sm font-medium text-gray-800" data-id="element-706">
                            Review payment details
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5" data-id="element-707">
                            Verify the merchant name (Gadgets Zone) and amount (
                            {formatPrice(totalAmount)}) are correct.
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-3" data-id="element-708">
                        <div className="w-7 h-7 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-709">
                          <ShieldCheckIcon className="w-4 h-4 text-green-600" data-id="element-710" />
                        </div>
                        <div data-id="element-711">
                          <p className="text-sm font-medium text-gray-800" data-id="element-712">
                            Approve the payment
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5" data-id="element-713">
                            Tap "Approve" or "Accept" and authenticate with your
                            PIN/biometrics. This page will update automatically.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>}
              </div>

              <p className="text-xs text-gray-500 animate-pulse" data-id="element-714">
                Waiting for your approval...
              </p>

              <button onClick={onCancel} className="mt-6 text-sm text-gray-500 hover:text-gray-700 underline" data-id="element-715">
                Cancel Payment
              </button>
            </div>}
        </div>}
    </div>;
}